import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { delay, map, share, shareReplay } from 'rxjs/operators';
import { LOCALSTORAGE_KEYS } from 'src/config';
import { environment } from '../../../environments/environment';
import { Pagination, PopulateRequest } from '../abstract/abstract.service';
import { AuthService } from '../auth.service';
import { fromEntries } from '../../utilities/json';
import { StatusCode, VGError } from '../../utilities/VGError';
import { BehaviorSubject, Observable, Subject, Subscription, defer, from, of } from 'rxjs';
import { clone, cloneDeep } from 'lodash';

interface Cache {
  invalidate: number; // timestamp
  data: any;
}

@Injectable({
  providedIn: 'root'
})
export class VlApiService {
  private getPendingRequests: Map<string, Observable<any>> = new Map();
  private getCache: Map<string, Cache> = new Map();

  constructor(private http: HttpClient, private authService: AuthService) { }

  public getMainRoute(): string {
    return environment.api_url;
  }

  public getHttpClient(): HttpClient {
    return this.http;
  }

  public invalidateStringifiedRequest(endpoint: string, config?: { populateRequest?: PopulateRequest, paramsRequest?: {} | Pagination, query?: any }): void {
    const params = this.constructParams(config);
    const stringifiedRequest: string = this.stringifyRequest(endpoint, params);

    this.getCache.delete(stringifiedRequest)
  }

  public invalidateEndpointRequest(endpoint: string): void {
    this.getCache.forEach((value: any, key: string) => {
      const split: string[] = key.split('?');
      if (split[0] == endpoint) {
        this.getCache.delete(key);
      }
    });
  }

  /**
   * GET request to the API
   * 
   * @param endpoint           API endpoint
   * @param populateRequest    PopulateRequest
   * @param body               Object or Pagination
   */
  public get<T>(endpoint: string, config?: { cache?: boolean, cacheTimeSecond?: number, populateRequest?: PopulateRequest, paramsRequest?: {} | Pagination, query?: any }): Observable<T> {
    const params = this.constructParams(config);
    const stringifiedRequest: string = this.stringifyRequest(endpoint, params);

    const cache: Cache = this.getCache.get(stringifiedRequest);
    let cacheData = cache?.data;

    if (cache) {
      // invalidate cache, removing it
      if (cache.invalidate < new Date().getTime()) {
        this.getCache.delete(stringifiedRequest);
        cacheData = null;
      }
      // valid cache, return it
      else {
        const subject$: BehaviorSubject<T> = new BehaviorSubject<T>(cacheData);

        setTimeout(() => {
          subject$.next(cloneDeep(cacheData)); // clone to ne be interfered
          subject$.complete();
        });

        return subject$.asObservable();
      }
    }

    if (!this.getPendingRequests.has(stringifiedRequest)) {
      const pending: Observable<T> = this._get<T>(endpoint, params, stringifiedRequest, config?.cache, config?.cacheTimeSecond).pipe(share())
      this.getPendingRequests.set(stringifiedRequest, pending);
      return pending;
    } else {
      return this.getPendingRequests.get(stringifiedRequest);
    }
  }

  private _get<T>(endpoint: string, params: HttpParams, requestString: string, cache: boolean = false, cacheTimeSecond?: number) {
    const observable: Observable<T> = this.http.get<T>(environment.api_url + endpoint, {
      headers: {
        company: environment.company_label
      },
      params: params,
      withCredentials: true,
      observe: 'response'
    })
      .pipe(
        map(response => response.body),
        map((response) => {
          /*if (cache) {
            const millis: number = cacheTimeSecond > 0 ? cacheTimeSecond * 1000 : 1000 * 120;
            this.getCache.set(requestString, {
              invalidate: (new Date().getTime() + millis),
              data: cloneDeep(response) // clone to not be interfered
            });
          }*/

          this.getPendingRequests.delete(requestString);

          return response;
        })
      ) as any;

    return observable;
  }

  public constructParams(config?: { populateRequest?: PopulateRequest, paramsRequest?: {} | Pagination, query?: any }) {
    let params = null;
    let paramsObj = null;

    if (config?.populateRequest) {
      let fields = "";

      if (config?.populateRequest.fieldParameters) {
        config?.populateRequest.fieldParameters.forEach(field => {
          fields += field;

          if (config?.populateRequest.fieldParameters.indexOf(field) < config?.populateRequest.fieldParameters.length - 1) {
            fields += ",";
          }
        });
      }

      paramsObj = {}

      if (config?.populateRequest.populateDataType) {
        paramsObj = {
          ...paramsObj,
          populateDataType: true
        }
      }

      if (config?.populateRequest.populateAllParametersMeta) {
        paramsObj = {
          ...paramsObj,
          populateAllParametersMeta: true
        }
      }

      if (config?.populateRequest.populateFileMedias) {
        paramsObj = {
          ...paramsObj,
          populateFileMedias: true
        }
      }

      if (fields.trim().length) {
        paramsObj["fields"] = fields;
      }

      const populate = config?.populateRequest?.populate
      if (populate) {
        paramsObj["populate"] = typeof populate == "string" ? populate : JSON.stringify(populate);
      }

      if (config?.populateRequest?.currencyId) {
        paramsObj["currencyId"] = localStorage.getItem(LOCALSTORAGE_KEYS.CURRENCY);;
      }

      if (config?.populateRequest?.countryId) {
        paramsObj["countryId"] = localStorage.getItem(LOCALSTORAGE_KEYS.COUNTRY);;
      }
    }

    if (config?.paramsRequest) {
      if (!config.paramsRequest['sort']) {
        delete config.paramsRequest['sort'];
      }

      /** Delete useless params with params there have null or undifined value */
      if (!config?.paramsRequest['filter']) {
        delete config.paramsRequest['filter']
      }

      if (!config?.paramsRequest['search']) {
        delete config.paramsRequest['search']
      } else {
        const search = config.paramsRequest['search'];
        config.paramsRequest['search'] = typeof search == "string" ? search : JSON.stringify(config.paramsRequest['search'])
      }

      paramsObj = {
        ...paramsObj,
        ...config.paramsRequest,
      }
    }

    if (config?.query) {
      paramsObj = {
        ...paramsObj,
        ...config.query
      }
    }

    if (paramsObj) {
      params = new HttpParams({
        fromObject: paramsObj
      });
    }

    return params;
  }

  public stringifyRequest(endpoint: string, params: HttpParams): string {
    return params ? endpoint + '?' + params.toString() : endpoint;
  }

  /**
   * POST request to the API
   * 
   * @param endpoint  API endpoint
   * @param body      Request body
   */
  public post<T>(endpoint: string, body?: any) {
    return this.http.post<T>(environment.api_url + endpoint, body, {
      withCredentials: true,
      observe: 'response',
      headers: {
        'company': environment.company_label
      }
    }).pipe(map(response => response.body))
  }

  /**
   * POST request to the API
   * 
   * @param endpoint  API endpoint
   * @param body      Request body
   */
  public patch<T>(endpoint: string, body?: any) {
    return this.http.patch<T>(environment.api_url + endpoint, body, {
      withCredentials: true,
      observe: 'response',
      headers: {
        'company': environment.company_label
      }
    }).pipe(map(response => response.body))
  }

  /**
   * PUT request to the API
   * 
   * @param endpoint  API endpoint
   * @param body      Request body
   */
  public put<T>(endpoint: string, body: any) {
    return this.http.put<T>(environment.api_url + endpoint, body, {
      withCredentials: true,
      observe: 'response',
      headers: {
        'company': environment.company_label
      }
    }).pipe(map(response => response.body))
  }

  /**
   * DELETE request to the API
   * 
   * @param endpoint  API endpoint
   * @param body      Body request
   */
  public delete<T>(endpoint: string) {
    return this.http.delete<T>(environment.api_url + endpoint, {
      withCredentials: true,
      observe: 'response',
      headers: {
        'company': environment.company_label
      }
    }).pipe(map(response => response.body))
  }
}

